import { Injectable } from '@angular/core';
import {BehaviorSubject, combineLatestWith, Observable} from 'rxjs';
import {filter, map, take, tap} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { ManagementStoreService } from './management-store.service';
import { SharedFacadeService } from '@emm/shared/services/shared-facade.service';
import { ManagementApiService } from '@emm/management/services/management-api.service';
import { FormGroup } from '@angular/forms';
import {ItemLocaleResponse} from '@emm/core/models/locale.model';
import {genericSort} from '@emm/core/util/sort.util';
import {GenericItem} from '@emm/core/models/context-menu-action.model';
import {Item} from '@emm/core/models/item.model';
import {SearchFilter} from '@emm/core/models/search-filter';
import {ManagementItemsStoreService} from '@emm/management/store/items/management-items-store.service';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';


@Injectable({ providedIn: 'root' })
export class ItemsFacadeService {
    removeMealFormfieldToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.REMOVE_MEAL_FORMFIELD);

    items$ = this.api.getAllItems();
    itemsAsGenericItems$ = this.items$.pipe(
        filter(RA.isNotNil),
        map((items) =>
            items.sort(genericSort(a => a['name']))
                .map(
                    (item) =>
                        ({
                            name: item.name,
                            tag: item.tag,
                            disabled: item.disabled,
                            source: item,
                        } as GenericItem<Item>)
                )
        )
    );

    attributes$ = this.api.getAllGlobalAttributes().pipe(
        tap(globalAttributes => {
            for (const attribute of globalAttributes) {
                if ((attribute?.type === 'SELECT' || attribute?.type === 'MULTI_SELECT' || attribute?.type === 'BOOL')
                    && attribute?.options) {
                    const lengthOfOptionsArray = attribute.options.length;
                    let fixedOption;
                    Object.values(attribute?.options).forEach((option: string) => {
                        let cleanString = this.removeInvalidCharacters(option);
                        if (attribute.type === 'BOOL') {
                            cleanString = cleanString.charAt(0).toUpperCase() + cleanString.slice(1);
                        }
                        fixedOption = {label: cleanString, value: cleanString};
                        attribute.options.push(fixedOption);
                        for (let i = 0; i < lengthOfOptionsArray; i++) {
                            if (typeof attribute.options[i] !== 'object') {
                                attribute.options.splice(i, 1);
                            }
                        }
                    });
                }
            }
        })
    );
    itemTypes$ = this.api.getAllLegacyItemTypes();
    form$ = new BehaviorSubject<FormGroup>(null);

    itemDetail$ = new BehaviorSubject<any>(null);
    itemLocaleDetail$ = new BehaviorSubject<any>(null);

    updateItem$ = new BehaviorSubject<any>(null);

    centerLoading$ = this.managementStore.isLoading$.items.pipe(
        combineLatestWith(this.managementStore.isLoading$.attributes),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );
    rightLoading$ = this.managementStore.isLoading$.base;
    anyLoading$ = this.centerLoading$.pipe(
        combineLatestWith(this.rightLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );

    constructor(
        private managementStore: ManagementStoreService,
        private itemStore: ManagementItemsStoreService,
        public api: ManagementApiService,
        private sharedFacade: SharedFacadeService,
        private launchDarklyService: LaunchDarklyService,) {}



    init(): void {
        this.sharedFacade.init();
        this.managementStore.dispatch.initItems();
    }



    selectItem(item: any): void {
        let itemDetail;
        if (item.tag) {
            this.api.getItem(item.tag).subscribe(result => {
                itemDetail = result;
                this.itemDetail$.next(itemDetail);
            });
        } else {
            this.api.getItem(item).subscribe(result => {
                itemDetail = result;
                this.itemDetail$.next(itemDetail);
            });
        }
    }

    removeInvalidCharacters(input: string): string {
        return input.replace(/[^a-zA-Z0-9_&!\/\-\s]/g, '');
    }

    searchItems(searchFilter: SearchFilter): void {
        this.itemStore.dispatch.searchItems(searchFilter);
    }



    // todo: eol?
    updateItem(tag: string, updates: any, dispatchReload: boolean): Observable<any> {
        this.api.updateItem(tag, updates).subscribe();
        this.updateItem$.next(dispatchReload);
        return this.updateItem$.pipe(take(1));
    }



    toggleEnabled(item: Item) {
        return this.api.setEnabled(item.tag, item.disabled);
    }

    startProgressBar(): void {
        this.managementStore.dispatch.startLoading();

    }
    stopProgressBar(): void {
        this.managementStore.dispatch.stopLoading();
    }

    openAddDialog() {
        this.itemStore.dispatch.openAddItemDialog();
    }



    deleteItem(item: Item) {
        this.api.deleteItem(item.tag).subscribe();
    }


    getItemLocale(tag: string, country: string, language: string): Observable<ItemLocaleResponse> {
        return this.api.getItemLocale(tag, country, language);
    }


    filterByAttributeGroup(filterAttributeGroup: string) {
        const filterByGroup$ = new BehaviorSubject<any>(null);
        const attributesExcludedFromForms = ['DISABLED', 'MEAL', 'LOCKED'];
        this.removeMealFormfieldToggle$.pipe(take(1)).subscribe(value => {
            if (value) {
                filterByGroup$.next(this.itemLocaleDetail$?.getValue().filter(attribute =>
                    (attribute?.metadata?.attributeGroup === filterAttributeGroup &&
                        !attributesExcludedFromForms.includes(attribute?.tag))));
            } else {
                filterByGroup$.next(this.itemLocaleDetail$?.getValue().filter(attribute =>
                    (attribute?.metadata?.attributeGroup === filterAttributeGroup && attribute?.tag !== 'DISABLED')));
            }
        });
        return filterByGroup$;
    }
}
